import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  CloudDownload as DownloadIcon,
  Description as ReportIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import TicketAttachmentUpload from '../../../../components/Tickets/AttachmentUpload';
import { getTokenFromStorage, formatDate } from '../../utils/ticketTransformers';

/**
 * Attachments tab for ticket files and intervention reports
 */
const AttachmentsTab = ({
  ticket,
  linkedInterventions = [],
  onFilesUploaded
}) => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();

  // Get standalone attachments (not linked to messages)
  const standaloneAttachments = ticket?.TicketAttachments?.filter(
    a => !a.messageId && !a.message_id
  ) || [];

  // Get completed interventions
  const completedInterventions = linkedInterventions.filter(i => i.status === 'completed');

  const handleDownloadAttachment = async (attachment) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
      const downloadUrl = attachment.file_id
        ? `${API_BASE_URL}/api/files/${attachment.file_id}/download?inline=false`
        : `https://files.caro.care${attachment.url}`;

      const token = await getTokenFromStorage('auth_token');
      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.originalName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      enqueueSnackbar(t ? t("Tickets.file_downloaded") : "File downloaded", { variant: 'success' });
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar(t ? t("Tickets.download_failed") : "Failed to download file", { variant: 'error' });
    }
  };

  const handleDownloadInterventionPdf = async (intervention) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
      const token = await getTokenFromStorage('auth_token');
      const response = await fetch(`${API_URL}/api/field-interventions/${intervention.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `intervention-report-${intervention.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
      } else {
        throw new Error('Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Intervention Reports Section */}
      {completedInterventions.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t ? t("Tickets.intervention_reports") : "Intervention Reports"}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {completedInterventions.map((intervention) => (
              <Grid item xs={12} sm={6} md={4} key={intervention.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ReportIcon color="success" />
                      <Typography variant="subtitle1" fontWeight="medium">
                        {t ? t("Tickets.intervention_report") : "Intervention Report"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {formatDate(intervention.completed_at || intervention.updatedAt)}
                    </Typography>
                    <Chip
                      label={intervention.outcome || 'Completed'}
                      size="small"
                      color="success"
                      sx={{ mb: 2 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadInterventionPdf(intervention)}
                      fullWidth
                    >
                      {t ? t("Tickets.download_pdf") : "Download PDF"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 3 }} />
        </>
      )}

      {/* Ticket Attachments Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t ? t("Tickets.ticket_attachments") : "Ticket Attachments"}
      </Typography>

      {/* Upload Section */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t ? t("Tickets.add_new_attachments") : "Add New Attachments"}
        </Typography>
        <TicketAttachmentUpload
          onFilesUploaded={onFilesUploaded}
          compact={true}
        />
      </Paper>

      {/* Attachments Grid */}
      {standaloneAttachments.length > 0 ? (
        <Grid container spacing={2}>
          {standaloneAttachments.map((attachment) => (
            <Grid item xs={12} sm={6} md={4} key={attachment.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <FileIcon color="primary" sx={{ mt: 0.5 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, wordBreak: 'break-word', mb: 0.5 }}
                      >
                        {attachment.originalName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t ? t("Tickets.size") : "Size: "}
                        {(attachment.size / 1024).toFixed(2)} KB
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t ? t("DeviceStatusDashboard.type") : "Type: "}
                        {attachment.mimetype}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t ? t("Tickets.uploaded") : "Uploaded: "}
                        {new Date(attachment.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadAttachment(attachment)}
                      fullWidth
                    >
                      {t ? t("Tickets.download") : "Download"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            opacity: 0.6
          }}
        >
          <FileIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
          <Typography variant="h6" gutterBottom>
            {t ? t("Tickets.no_attachments") : "No Attachments"}
          </Typography>
          <Typography variant="body2">
            {t ? t("Tickets.this_ticket_doesnt_have_any_attachments") : "This ticket doesn't have any attachments yet."}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AttachmentsTab;
